/**
 * SimpleAuthContext - Authentication Context for Wyra
 *
 * @description Manages the global authentication state and provides auth methods
 * following the principles of the Wyra Roadmap authentication flow. This context
 * supports Firebase Custom Claims for role-based access control (RBAC) with
 * fallback to Firestore for backward compatibility.
 *
 * @features
 * - Google OAuth authentication
 * - Email/password authentication
 * - Phone number authentication (SMS verification)
 * - Custom Claims from Firebase ID tokens
 * - Role-based permissions (0-100 scale)
 * - Token refresh and validation
 * - User profile management
 *
 * @example
 * ```jsx
 * import { useSimpleAuth } from './features/auth/SimpleAuthContext'
 *
 * function MyComponent() {
 *   const { currentUser, login, logout, isAdmin } = useSimpleAuth()
 *   return <div>{currentUser?.displayName}</div>
 * }
 * ```
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  getIdToken,
  getIdTokenResult,
} from 'firebase/auth'
import {
  auth,
  db,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  saveUserData,
  getUserData,
  signInWithGooglePopup,
  createRecaptchaVerifier,
  sendPhoneVerificationCode,
  verifyPhoneCode,
  completePhoneAuth,
} from '../../services/firebase'

const SimpleAuthContext = createContext()

/**
 * SimpleAuthProvider - Authentication Provider Component
 *
 * @description Wraps the application and provides authentication state and methods
 * to all child components. Uses Firebase Authentication with Google OAuth and
 * email/password support. Reads Custom Claims from ID tokens for role-based access.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 *
 * @returns {JSX.Element} Provider component with auth context
 *
 * @example
 * ```jsx
 * import { SimpleAuthProvider } from './features/auth/SimpleAuthContext'
 *
 * function App() {
 *   return (
 *     <SimpleAuthProvider>
 *       <YourAppComponents />
 *     </SimpleAuthProvider>
 *   )
 * }
 * ```
 */
export function SimpleAuthProvider({ children }) {
  // State management
  const [currentUser, setCurrentUser] = useState(null)
  const [isModerator, setIsModerator] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasShownWelcome, setHasShownWelcome] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [primaryRole, setPrimaryRole] = useState(10) // Nivel de rol (0-100)
  const [roleName, setRoleName] = useState('USER') // Nombre del rol
  const [customClaims, setCustomClaims] = useState(null) // Custom claims completos

  // Computed states
  // Admin: primaryRole >= 100, User: primaryRole = 0
  const isAdmin = primaryRole >= 100 || permissions.includes('manage:roles')
  const hasValidSession = currentUser && currentUser.emailVerified

  /**
   * Update user state and permissions
   * @param {object|null} userData - User data from auth
   * @param {object|null} claims - Custom claims from token
   */
  const updateUserState = useCallback((userData, claims = null) => {
    if (userData && typeof userData === 'object') {
      // Ensure we have a valid user object
      setCurrentUser(userData)

      // Priorizar Custom Claims del token sobre Firestore
      if (claims) {
        // Usar custom claims del token (nueva arquitectura)
        setPrimaryRole(claims.primaryRole || 10)
        setRoleName(claims.roleName || 'USER')
        setCustomClaims(claims)

        // Extraer permisos de los claims
        const claimPermissions = claims.permissions || []
        setPermissions(claimPermissions)

        // Determinar si es moderador (nivel >= 60)
        setIsModerator((claims.primaryRole || 10) >= 60)
      } else {
        // Fallback a datos de Firestore (backward compatibility)
        setIsModerator(userData.role === 'moderator')

        // Extraer permisos de roles de Firestore
        const userPermissions = []
        if (userData.roles) {
          userData.roles.forEach(role => {
            switch (role) {
              case 'moderator':
                userPermissions.push(
                  'read:own_profile',
                  'update:own_profile',
                  'read:all_profiles',
                  'moderate:content',
                  'manage:events',
                  'manage:groups'
                )
                break
              case 'admin':
                userPermissions.push(
                  'read:own_profile',
                  'update:own_profile',
                  'delete:own_profile',
                  'read:all_profiles',
                  'moderate:content',
                  'manage:events',
                  'manage:groups',
                  'delete:users',
                  'manage:roles',
                  'system:config'
                )
                break
              default:
                userPermissions.push('read:own_profile', 'update:own_profile', 'delete:own_profile')
            }
          })
        } else {
          // Default permissions based on single role
          if (userData.role === 'moderator') {
            userPermissions.push(
              'read:own_profile',
              'update:own_profile',
              'read:all_profiles',
              'moderate:content',
              'manage:events',
              'manage:groups'
            )
          } else if (userData.role === 'admin') {
            userPermissions.push(
              'read:own_profile',
              'update:own_profile',
              'delete:own_profile',
              'read:all_profiles',
              'moderate:content',
              'manage:events',
              'manage:groups',
              'delete:users',
              'manage:roles',
              'system:config'
            )
          } else {
            userPermissions.push('read:own_profile', 'update:own_profile')
          }
        }
        setPermissions(userPermissions)
        setPrimaryRole(userData.primaryRole || 10)
        setRoleName(userData.role?.toUpperCase() || 'USER')
        setCustomClaims(null)
      }
    } else {
      setCurrentUser(null)
      setIsModerator(false)
      setPermissions([])
      setPrimaryRole(10)
      setRoleName('USER')
      setCustomClaims(null)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      console.log('Estado de Auth:', user) // Debug del estado de autenticación

      setIsLoading(true)

      try {
        if (user) {
          console.log('Usuario detectado:', {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          })

          // Get user data from Firestore FIRST (to check for role updates)
          const userDoc = await getDoc(doc(db, 'users', user.uid))
          const userData = userDoc.data()

          // Obtener ID Token Result con Custom Claims (nueva arquitectura)
          let idTokenResult = await getIdTokenResult(user, false)
          let claims = idTokenResult?.claims

          console.log('Custom Claims del token (primer intento):', {
            primaryRole: claims?.primaryRole,
            roleName: claims?.roleName,
            hasCustomClaims: !!claims?.primaryRole,
            permissionsCount: claims?.permissions?.length || 0
          })

          // CRITICAL: Verificar si hay discrepancia entre Firestore y Token
          // Esto fuerza el refresh del token cuando los Custom Claims fueron actualizados
          const firestoreRole = userData?.primaryRole || 10
          const tokenRole = claims?.primaryRole || 10

          if (firestoreRole !== tokenRole) {
            console.warn(`⚠️ DISCREPANCIA DETECTADA: Firestore=${firestoreRole}, Token=${tokenRole}`)
            console.log('🔄 FORZANDO REFRESH DEL TOKEN...')

            // Recargar el usuario desde Firebase Auth
            await user.reload()

            // Forzar refresh del token con los nuevos Custom Claims
            idTokenResult = await getIdTokenResult(user, true)
            claims = idTokenResult?.claims

            console.log('✅ Custom Claims después del refresh:', {
              primaryRole: claims?.primaryRole,
              roleName: claims?.roleName,
              hasCustomClaims: !!claims?.primaryRole,
              permissionsCount: claims?.permissions?.length || 0
            })

            // Recargar datos de Firestore después del refresh para sincronizar
            const refreshedUserDoc = await getDoc(doc(db, 'users', user.uid))
            if (refreshedUserDoc.exists) {
              Object.assign(userData, refreshedUserDoc.data())
            }
          }

          // Create user if doesn't exist in Firestore
          if (!userData) {
            console.log('Usuario no existe en Firestore, creando perfil...')
            await saveUserData(user.uid, {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
              emailVerified: user.emailVerified,
              createdAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            })
          }

          console.log('Datos de Firestore:', userData)

          const extendedUser = {
            ...user,
            id: user.uid,
            email: user.email,
            displayName: userData?.displayName || user.displayName,
            photoURL: user.photoURL || userData?.photoURL,  // Priorizar Google sobre Firestore (usar siempre la foto de Google)
            emailVerified: user.emailVerified,
            role: claims?.roleName || userData?.role || 'user', // Priorizar claims
            primaryRole: claims?.primaryRole || userData?.primaryRole || 10,
            status: userData?.status || 'active', // Importante: incluir estado de autoridad Wyra
            preferences: userData?.preferences || {},
            bio: userData?.bio,
            location: userData?.location,
            createdAt: userData?.createdAt,
            lastLoginAt: serverTimestamp(),
          }

          console.log('Usuario extendido:', extendedUser)

          // Update user state con custom claims del token
          updateUserState(extendedUser, claims)

          // Check if this is the first time the user is logged in
          const userId = localStorage.getItem('userId')
          if (!userId) {
            // First time user - show welcome message
            const welcomeMessage = `¡Bienvenido ${extendedUser.displayName || 'Usuario'}!`
            console.log(welcomeMessage)

            // Store user ID to detect returning users
            localStorage.setItem('userId', extendedUser.uid)
            localStorage.setItem(`user-${extendedUser.uid}`, 'true')

            setHasShownWelcome(true)
          }
        } else {
          console.log('No hay usuario autenticado')
          updateUserState(null)
        }
      } catch (error) {
        console.error('❌ Error en estado de autenticación:', error)

        // Specific error handling for Firestore issues
        if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
          setAuthError('Error de conexión con la base de datos. Por favor, verifica tu conexión a internet.')
        } else if (error.code === 'permission-denied') {
          setAuthError('No tienes permisos para acceder a la base de datos. Contacta al administrador.')
        } else if (error.message.includes('Failed to get document')) {
          // User exists in Auth but not in Firestore - create basic profile
          console.log('⚠️ Usuario existe en Auth pero no en Firestore, creando perfil básico...')

          try {
            if (user) {
              // Intentar obtener claims del token primero
              let claims = null
              try {
                const idTokenResult = await getIdTokenResult(user, true)
                claims = idTokenResult?.claims
              } catch (claimsError) {
                console.warn('No se pudieron obtener claims del token:', claimsError.message)
              }

              const basicUserData = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || user.email.split('@')[0],
                photoURL: user.photoURL,
                emailVerified: user.emailVerified,
                role: claims?.roleName || 'user',
                primaryRole: claims?.primaryRole || 10,
                status: 'active',
                preferences: {
                  notifications: true,
                  theme: 'light',
                  language: 'es'
                },
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp()
              }

              await setDoc(doc(db, 'users', user.uid), basicUserData)
              console.log('✅ Perfil básico creado en Firestore')

              // Crear extendedUser priorizando datos de Auth
              const extendedUser = {
                ...user,
                ...basicUserData,
                // Siempre usar photoURL de Auth si existe
                photoURL: user.photoURL || basicUserData?.photoURL,
              }
              updateUserState(extendedUser, claims)
            }
          } catch (createError) {
            console.error('❌ Error al crear perfil básico:', createError)
            setAuthError('Error al configurar tu perfil. Por favor, recarga la página.')
            updateUserState(null)
          }
        } else {
          setAuthError(error.message || 'Error de autenticación')
          updateUserState(null)
        }
      } finally {
        // IMPORTANT: Ensure sync is complete before setting loading to false
        // Add a delay to ensure all state updates have propagated
        console.log('⏳ Finalizando loading, usuario detectado:', !!user)
        setTimeout(() => {
          setIsLoading(false)
          console.log('✅ Estado de carga finalizado:', { isLoading: false, currentUser: user?.email || 'none' })
        }, 150) // Aumentado a 150ms para asegurar sincronización
      }
    })

    return () => unsubscribe()
  }, [])

  const login = async (email = null, password = null, onSuccessCallback = null) => {
    try {
      if (email && password) {
        // Email/Password login (Legacy)
        const result = await signInWithEmailAndPassword(auth, email, password)

        // Get or create user in Firestore
        const userDoc = await getDoc(doc(db, 'users', result.user.uid))
        const userData = userDoc.data()

        if (!userData) {
          // Create user if doesn't exist
          await saveUserData(result.user.uid, {
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL,
            emailVerified: result.user.emailVerified,
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
          })
        }

        // Crear extendedUser priorizando datos de Auth sobre Firestore
        const extendedUser = {
          ...result.user,
          ...userData,
          // Usar photoURL de Auth si existe
          photoURL: result.user.photoURL || userData?.photoURL,
        }
        updateUserState(extendedUser)

        if (onSuccessCallback) {
          onSuccessCallback(extendedUser, userData)
        }

        return extendedUser
      } else {
        // Google login - use POPUP (better for development, avoids Service Worker issues)
        console.log('🚀 INICIANDO LOGIN CON GOOGLE (POPUP)')

        const result = await signInWithGooglePopup()

        if (result && result.user) {
          console.log('✅ Usuario autenticado con Google:', result.user.email)

          // Get or create user in Firestore
          const userDoc = await getDoc(doc(db, 'users', result.user.uid))
          const userData = userDoc.data()

          if (!userData) {
            // Create user if doesn't exist
            await saveUserData(result.user.uid, {
              uid: result.user.uid,
              email: result.user.email,
              displayName: result.user.displayName,
              photoURL: result.user.photoURL,
              emailVerified: result.user.emailVerified,
              createdAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            })
          }

          // Crear extendedUser priorizando datos de Google sobre Firestore
          const extendedUser = {
            ...result.user,
            ...userData,
            // Siempre usar la foto de Google si existe
            photoURL: result.user.photoURL || userData?.photoURL,
          }

          if (onSuccessCallback) {
            onSuccessCallback(extendedUser, userData)
          }

          return extendedUser
        }

        return null
      }
    } catch (error) {
      console.error('❌ Error en login:', error)
      throw error
    }
  }

  const register = async (email, password, onSuccessCallback = null) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)

      // Create user profile in Firestore
      const newUserProfile = {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName || email.split('@')[0],
        photoURL: result.user.photoURL || null,
        emailVerified: result.user.emailVerified,
        role: 'user',
        preferences: {
          notifications: true,
          theme: 'light',
        },
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      }

      await setDoc(doc(db, 'users', result.user.uid), newUserProfile)

      // Call success callback with user data
      if (onSuccessCallback) {
        onSuccessCallback(result.user)
      }

      return result.user
    } catch (error) {
      console.error('Error with email registration:', error)
      throw error
    }
  }

  /**
   * Send phone verification code (SMS)
   * @param {string} phoneNumber - Phone number in format: +1234567890
   * @param {string} containerId - ID of the button/container element for reCAPTCHA
   * @returns {Promise<object>} Confirmation result
   */
  const sendPhoneCode = useCallback(async (phoneNumber, containerId = 'phone-auth-button') => {
    try {
      console.log('📱 Enviando código SMS al teléfono:', phoneNumber)

      // Create reCAPTCHA verifier
      const appVerifier = createRecaptchaVerifier(containerId, {
        size: 'invisible',
        callback: (response) => {
          console.log('reCAPTCHA resuelto:', response)
        },
        'expired-callback': () => {
          console.warn('reCAPTCHA expiró')
          setAuthError('El reCAPTCHA expiró. Intenta de nuevo.')
        }
      })

      // Send verification code
      const confirmationResult = await sendPhoneVerificationCode(phoneNumber, appVerifier)

      // Store confirmation result globally for verification step
      if (typeof window !== 'undefined') {
        window.phoneConfirmationResult = confirmationResult
      }

      console.log('✅ Código SMS enviado')
      return { success: true, confirmationResult }
    } catch (error) {
      console.error('❌ Error enviando código SMS:', error)
      setAuthError(error.message || 'Error al enviar código SMS')
      throw error
    }
  }, [])

  /**
   * Verify phone code and complete authentication
   * @param {string} verificationCode - 6-digit code from SMS
   * @param {string} phoneNumber - Phone number used for authentication
   * @returns {Promise<object>} Authenticated user
   */
  const verifyPhone = useCallback(async (verificationCode, phoneNumber) => {
    try {
      console.log('🔐 Verificando código SMS...')

      // Get confirmation result from window
      const confirmationResult = typeof window !== 'undefined' ? window.phoneConfirmationResult : null

      if (!confirmationResult) {
        throw new Error('No hay una verificación de teléfono en progreso. Primero solicita el código SMS.')
      }

      // Verify the code
      const user = await verifyPhoneCode(confirmationResult, verificationCode)

      // Complete phone auth (create/update Firestore profile)
      const userProfile = await completePhoneAuth(user, phoneNumber)

      console.log('✅ Autenticación con teléfono completada')

      // The onAuthStateChanged listener will handle the rest
      return { success: true, user, userProfile }
    } catch (error) {
      console.error('❌ Error verificando código:', error)
      setAuthError(error.message || 'Código de verificación incorrecto')
      throw error
    } finally {
      // Clean up global confirmation result
      if (typeof window !== 'undefined') {
        window.phoneConfirmationResult = null
      }
    }
  }, [])

  /**
   * Complete phone authentication flow (send code + verify)
   * @param {string} phoneNumber - Phone number in format: +1234567890
   * @param {string} verificationCode - 6-digit code from SMS
   * @param {string} containerId - ID of the button for reCAPTCHA
   * @returns {Promise<object>} Authenticated user
   */
  const loginWithPhone = useCallback(async (phoneNumber, verificationCode, containerId = 'phone-auth-button') => {
    try {
      console.log('📱 Iniciando autenticación con teléfono...')

      // Send code first
      await sendPhoneCode(phoneNumber, containerId)

      // Then verify (normally these would be separate user actions)
      const result = await verifyPhone(verificationCode, phoneNumber)

      return result
    } catch (error) {
      console.error('❌ Error en autenticación con teléfono:', error)
      throw error
    }
  }, [sendPhoneCode, verifyPhone])

  const logout = async () => {
    try {
      console.log('🚪 INICIANDO LOGOUT')

      // Limpiar estado del contexto primero
      updateUserState(null)
      setAuthError(null)

      // Ejecutar logout de Firebase
      await signOut(auth)

      console.log('✅ LOGOUT COMPLETADO')
    } catch (error) {
      console.error('❌ Error en logout:', error)
      setAuthError(error.message || 'Error al cerrar sesión')

      // Forzar limpieza de estado local incluso si falló el logout
      updateUserState(null)

      throw error
    }
  }

  // Token validation and refresh logic
  const validateToken = async () => {
    if (!currentUser) return false

    try {
      const idToken = await getIdToken(currentUser, true)
      return !!idToken
    } catch (error) {
      console.error('Token validation failed:', error)
      return false
    }
  }

  const refreshToken = async () => {
    if (!currentUser) return null

    try {
      const newToken = await getIdToken(currentUser, true)
      return newToken
    } catch (error) {
      console.error('Token refresh failed:', error)
      return null
    }
  }

  /**
   * Force token refresh and update user state with new claims
   * Use this after role changes to immediately update permissions
   * @returns {Promise<object|null>} Updated claims or null
   */
  const forceTokenRefresh = useCallback(async () => {
    if (!currentUser) {
      console.warn('No hay usuario autenticado para refrescar token')
      return null
    }

    // Verificar que currentUser sea un objeto válido de Firebase con los métodos necesarios
    if (!currentUser.uid || typeof currentUser.getIdTokenResult !== 'function') {
      console.warn('⚠️ currentUser no es un objeto de Firebase válido:', {
        hasUid: !!currentUser?.uid,
        hasGetIdTokenResult: typeof currentUser?.getIdTokenResult === 'function',
        hasReload: typeof currentUser?.reload === 'function'
      })
      return null
    }

    try {
      console.log('🔄 Forzando refresh del token...')

      // Intentar recargar usuario si el método existe
      if (typeof currentUser.reload === 'function') {
        try {
          await currentUser.reload()
        } catch (reloadError) {
          console.warn('⚠️ No se pudo recargar el usuario, continuando con refresh de token:', reloadError)
        }
      }

      // Obtener nuevo token con claims actualizados (forceRefresh = true)
      const idTokenResult = await getIdTokenResult(currentUser, true)
      const newClaims = idTokenResult?.claims

      console.log('✅ Nuevos Custom Claims:', {
        primaryRole: newClaims?.primaryRole,
        roleName: newClaims?.roleName,
        permissionsCount: newClaims?.permissions?.length || 0
      })

      // Actualizar estado del usuario con los nuevos claims
      if (newClaims?.primaryRole) {
        setPrimaryRole(newClaims.primaryRole)
        setRoleName(newClaims.roleName || 'USER')
        setCustomClaims(newClaims)
        setPermissions(newClaims.permissions || [])
        setIsModerator(newClaims.primaryRole >= 60)
      }

      return newClaims
    } catch (error) {
      console.error('❌ Error forzando refresh del token:', error)
      return null
    }
  }, [currentUser])

  /**
   * Check if user has specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} User has permission
   */
  const hasPermission = useCallback(
    permission => {
      return permissions.includes(permission)
    },
    [permissions]
  )

  /**
   * Send email verification
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  const sendEmailVerification = useCallback(async (onSuccess, onError) => {
    try {
      // This would need to be implemented with Firebase email verification
      // For now, we'll just log it
      console.log('Email verification requested')
      if (onSuccess) onSuccess()
    } catch (error) {
      const errorMessage = error.message || 'Error al enviar verificación'
      if (onError) onError(errorMessage)
    }
  }, [])

  /**
   * Update user profile
   * @param {object} profileData - Profile data to update
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  const updateProfile = useCallback(
    async (profileData, onSuccess, onError) => {
      if (!currentUser) {
        const error = 'No hay usuario autenticado'
        setAuthError(error)
        if (onError) onError(error)
        return
      }

      try {
        // This would need to be implemented with Firebase update functions
        const updatedUser = { ...currentUser, ...profileData }
        updateUserState(updatedUser)

        if (onSuccess) onSuccess(updatedUser)
      } catch (error) {
        const errorMessage = error.message || 'Error al actualizar perfil'
        setAuthError(errorMessage)
        if (onError) onError(errorMessage)
      }
    },
    [currentUser, updateUserState]
  )

  /**
   * Refresh user data from Firestore
   * This is useful when user data is updated outside the auth context (e.g., profile photo update)
   * @returns {Promise<object>} Updated user data
   */
  const refreshUserData = useCallback(async () => {
    if (!currentUser) {
      console.warn('No hay usuario autenticado para refrescar')
      return null
    }

    try {
      console.log('🔄 Refrescando datos del usuario desde Firestore...')

      // Recargar usuario desde Firebase Auth primero
      await currentUser.reload()
      console.log('✅ Usuario recargado desde Firebase Auth')

      // Obtener datos actualizados de Firestore
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid))
      const userData = userDoc.data()

      console.log('📋 Datos de Firestore:', userData)

      // Crear usuario extendido con datos actualizados
      const extendedUser = {
        ...currentUser,
        id: currentUser.uid,
        email: currentUser.email,
        displayName: userData?.displayName || currentUser.displayName,
        photoURL: currentUser.photoURL || userData?.photoURL,  // Priorizar Google sobre Firestore
        emailVerified: currentUser.emailVerified,
        role: userData?.role || 'user',
        primaryRole: userData?.primaryRole || 10,  // Leer primaryRole de Firestore
        status: userData?.status || 'active',
        preferences: userData?.preferences || {},
        bio: userData?.bio,
        location: userData?.location,
        createdAt: userData?.createdAt,
        lastLoginAt: serverTimestamp(),
      }

      // Actualizar estado del contexto con primaryRole de Firestore
      updateUserState(extendedUser, null)  // null = no usar claims del token, usar Firestore

      console.log('✅ Datos del usuario refrescados correctamente')
      return extendedUser

    } catch (error) {
      console.error('❌ Error refrescando datos del usuario:', error)
      setAuthError(error.message || 'Error al refrescar datos del usuario')
      return null
    }
  }, [currentUser, updateUserState])

  // Clear error after 5 seconds
  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        setAuthError(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [authError])

  // Ensure useCallback dependencies are included
  useEffect(() => {
    // Re-run user state update when dependencies change
    // This ensures the context stays in sync
  }, [updateUserState])

  const value = {
    // State
    currentUser,
    isAuthenticated: !!currentUser,
    isModerator,
    isAdmin,
    isLoading,
    authError,
    permissions,
    hasValidSession,
    hasShownWelcome,
    primaryRole,      // Nivel de rol (0-100)
    roleName,        // Nombre del rol (ADMIN, MODERATOR, etc.)
    customClaims,    // Custom claims completos del token

    // Methods
    login,
    register,
    logout,
    validateToken,
    refreshToken,
    forceTokenRefresh,  // Nuevo método para forzar refresh y obtener nuevos claims
    hasPermission,
    sendEmailVerification,
    updateProfile,
    refreshUserData,  // Nuevo método para refrescar datos del usuario
    sendPhoneCode,    // Enviar código SMS al teléfono
    verifyPhone,      // Verificar código SMS y completar autenticación
    loginWithPhone,   // Flujo completo de autenticación con teléfono

    // Clear error method
    clearAuthError: () => setAuthError(null),
  }

  return <SimpleAuthContext.Provider value={value}>{children}</SimpleAuthContext.Provider>
}

/**
 * Hook to use authentication context
 * @returns {object} Authentication context value
 */
export const useSimpleAuth = () => {
  const context = useContext(SimpleAuthContext)

  if (!context) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider')
  }

  return context
}

/**
 * Higher-order component for protected routes
 * @param {React.Component} Component - Component to protect
 * @param {string|Array} requiredPermissions - Required permissions
 * @returns {React.Component} Protected component
 */
export function withAuth(Component, requiredPermissions = null) {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, hasPermission, currentUser } = useSimpleAuth()

    // Check authentication
    if (!isAuthenticated) {
      return <div>Please sign in to continue</div>
    }

    // Check permissions if required
    if (requiredPermissions) {
      const permissions = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions]

      const hasAllPermissions = permissions.every(permission => hasPermission(permission))

      if (!hasAllPermissions) {
        return <div>You don't have permission to access this page</div>
      }
    }

    return <Component {...props} user={currentUser} />
  }
}
